import React, { useMemo } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useStore, selectedApp } from '../../../store';
import {
  useSystemStatus,
  type StatusLevel,
} from '../../../~reusables/hooks/useSystemStatus';
import { getDocGroups, STATUS_PAGE_URL } from './docs';
import { IconButton, MenuRow, MenuRowText, StatusDot } from './NavStyles';

export const statusColors: Record<StatusLevel, string> = {
  operational: '#22c55e',
  degraded_performance: '#f59e0b',
  partial_outage: '#f97316',
  full_outage: '#ef4444',
  maintenance: '#3b82f6',
};

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden
    style={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
    }}
  />
);

const externalLink = (url: string, label: React.ReactNode) => (
  <a href={url} target="_blank" rel="noopener noreferrer">
    {label}
  </a>
);

export const HelpMenu: React.FC = () => {
  const { apps, currentApp } = useStore((state) => ({
    apps: state.apps,
    currentApp: selectedApp(state),
  }));
  const { level, message, incidents, maintenances } = useSystemStatus();

  const items = useMemo<MenuProps['items']>(() => {
    const docGroups = getDocGroups(apps, currentApp);
    const menuItems: NonNullable<MenuProps['items']> = docGroups.map(
      (group) => ({
        key: `docs-${group.key}`,
        type: 'group' as const,
        label: docGroups.length > 1 ? `${group.label} docs` : 'Docs',
        children: group.links.map((link) => ({
          key: link.key,
          label: externalLink(link.url, link.label),
        })),
      }),
    );

    const statusChildren: NonNullable<MenuProps['items']> = [
      {
        key: 'status',
        label: externalLink(
          STATUS_PAGE_URL,
          <MenuRow>
            <Dot color={statusColors[level]} />
            <MenuRowText>{message}</MenuRowText>
          </MenuRow>,
        ),
      },
      ...incidents.map((incident) => ({
        key: `incident-${incident.id}`,
        label: externalLink(
          incident.url || STATUS_PAGE_URL,
          <MenuRow>
            <MenuRowText>{incident.name}</MenuRowText>
          </MenuRow>,
        ),
      })),
      ...maintenances.map((maintenance) => ({
        key: `maintenance-${maintenance.id}`,
        label: externalLink(
          maintenance.url || STATUS_PAGE_URL,
          <MenuRow>
            <MenuRowText>{maintenance.name}</MenuRowText>
          </MenuRow>,
        ),
      })),
    ];

    menuItems.push(
      { type: 'divider', key: 'divider-status' },
      {
        key: 'status-group',
        type: 'group',
        label: 'Status',
        children: statusChildren,
      },
    );
    return menuItems;
  }, [apps, currentApp, level, message, incidents, maintenances]);

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{ items, style: { minWidth: 260 } }}
    >
      <IconButton
        type="button"
        aria-label={`Help and status: ${message}`}
        title="Help and status"
      >
        <QuestionCircleOutlined style={{ fontSize: 17 }} />
        <StatusDot $color={statusColors[level]} />
      </IconButton>
    </Dropdown>
  );
};
